import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { Submission, Task, Participant, ParticipantGroup } from '../types';

interface AnalyticsChartsProps {
    submissions: Submission[];
    tasks: Task[];
    participants: Participant[];
    participantGroups: ParticipantGroup[];
}

export const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({ submissions, tasks, participants, participantGroups }) => {
    const [completionViewMode, setCompletionViewMode] = useState<'tasks' | 'groups'>('tasks');

    // Process data for Timeline (Submissions per Day)
    const timelineData = useMemo(() => {
        const countsByDate = new Map<string, number>();
        submissions.forEach(sub => {
            // Assuming createdAt or timestamp exists. 'timestamp' works.
            const dateRaw = sub.timestamp || sub.createdAt || new Date().toISOString();
            const dateOrTimestampStr = typeof dateRaw === 'string' ? dateRaw : dateRaw.toDate ? dateRaw.toDate().toISOString() : new Date().toISOString();
            const dateKey = dateOrTimestampStr.split('T')[0];
            countsByDate.set(dateKey, (countsByDate.get(dateKey) || 0) + 1);
        });

        const sortedDates = Array.from(countsByDate.keys()).sort();
        let cumulative = 0;

        return sortedDates.map(date => {
            const dailyCount = countsByDate.get(date)!;
            cumulative += dailyCount;
            return {
                date,
                daily: dailyCount,
                cumulative
            };
        });
    }, [submissions]);

    // Process data for Task Completion (Drop-off proxy)
    const taskCompletionData = useMemo(() => {
        return tasks.map(task => {
            const submissionsForTask = submissions.filter(s => s.taskId === task.id).length;
            let assignedCount = participants.length; // Default to all
            if (task.assignedParticipantIds && task.assignedParticipantIds.length > 0) {
                assignedCount = task.assignedParticipantIds.length;
            }
            return {
                name: (task.title || task.prompt).substring(0, 20) + '...',
                target: assignedCount,
                completed: submissionsForTask
            };
        });
    }, [tasks, submissions, participants]);

    // Process data for Group Completion (Drop-off proxy by Task customGroup)
    const groupCompletionData = useMemo(() => {
        const groupMap = new Map<string, { target: number, completed: number }>();

        tasks.forEach(task => {
            const groupName = task.customGroup || 'Ungrouped';
            if (!groupMap.has(groupName)) {
                groupMap.set(groupName, { target: 0, completed: 0 });
            }

            const groupStats = groupMap.get(groupName)!;

            const submissionsForTask = submissions.filter(s => s.taskId === task.id).length;
            let assignedCount = participants.length;
            if (task.assignedParticipantIds && task.assignedParticipantIds.length > 0) {
                assignedCount = task.assignedParticipantIds.length;
            }

            groupStats.target += assignedCount;
            groupStats.completed += submissionsForTask;
        });

        return Array.from(groupMap.entries())
            .filter(([name]) => name !== 'Ungrouped') // Optional: hide ungrouped or keep it
            .map(([name, stats]) => ({
                name: name.substring(0, 20) + (name.length > 20 ? '...' : ''),
                target: stats.target,
                completed: stats.completed
            }));
    }, [tasks, submissions, participants]);

    const activeChartData = completionViewMode === 'tasks' ? taskCompletionData : groupCompletionData;
    const chartWidth = Math.max(800, activeChartData.length * 60); // Dynamic width to force scroll

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mt-8">

            {/* Timeline Chart */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-app-border flex flex-col h-[340px]">
                <div className="mb-4">
                    <h3 className="text-base font-bold text-text-ink">Submissions Over Time</h3>
                    <p className="text-xs text-text-muted">Cumulative research submissions</p>
                </div>
                <div className="flex-1 min-h-0 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timelineData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#7BBFAF" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#7BBFAF" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fill: '#9ca3af', fontSize: 10 }} dy={10} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fill: '#9ca3af', fontSize: 10 }} />
                            <Tooltip
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                                itemStyle={{ fontSize: '12px', padding: '2px 0' }}
                                labelStyle={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}
                                cursor={{ stroke: '#7BBFAF', strokeWidth: 1, strokeDasharray: '5 5' }}
                            />
                            <Area type="monotone" dataKey="cumulative" name="Total Submissions" stroke="#7BBFAF" strokeWidth={3} fillOpacity={1} fill="url(#colorCumulative)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Task Completion Drop-off Chart */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-app-border flex flex-col h-[340px]">
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <h3 className="text-base font-bold text-text-ink">Completion (Drop-off Rate)</h3>
                        <p className="text-xs text-text-muted">Target vs Completed</p>
                    </div>

                    {/* View Toggle */}
                    <div className="flex items-center bg-neutral-100 p-1 rounded-lg">
                        <button
                            onClick={() => setCompletionViewMode('tasks')}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${completionViewMode === 'tasks' ? 'bg-white text-action shadow-sm' : 'text-text-muted hover:text-text-ink'
                                }`}
                        >
                            Tasks
                        </button>
                        <button
                            onClick={() => setCompletionViewMode('groups')}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${completionViewMode === 'groups' ? 'bg-white text-action shadow-sm' : 'text-text-muted hover:text-text-ink'
                                }`}
                        >
                            Groups
                        </button>
                    </div>
                </div>

                {/* Scrollable Container */}
                <div className="flex-1 min-h-0 w-full overflow-x-auto relative">
                    <div style={{ width: chartWidth, height: '100%', minWidth: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={activeChartData} margin={{ top: 10, right: 30, left: 0, bottom: 15 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: '#9ca3af', fontSize: 9 }} dy={10} />
                                <YAxis tickLine={false} axisLine={false} tick={{ fill: '#9ca3af', fontSize: 10 }} />
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                                    itemStyle={{ fontSize: '12px', padding: '2px 0' }}
                                    labelStyle={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}
                                    cursor={{ fill: '#f3f4f6' }}
                                />
                                <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '11px' }} iconType="circle" />
                                <Bar dataKey="target" name="Assigned Participants" fill="#94a3b8" radius={[10, 10, 10, 10]} barSize={8} />
                                <Bar dataKey="completed" name="Actual Submissions" fill="#6E7A5A" radius={[10, 10, 10, 10]} barSize={8} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default AnalyticsCharts;
